import { useEffect, useMemo, useState } from 'react';
import { Form, ResourceContextProvider, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import {
  Box, InputAdornment, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslate } from 'ra-core';
import authProvider from '../../../utils/authProvider';
import { typedHttpClient } from '../../../utils/httpClient';
import {
  AppConfigConfigType,
  AppConfigDefinitionType,
} from '../../../lib/constants/typedApiReturnTypes';
import { ClientConfigFormGenerator } from '../../../lib/clientConfigFormGenerator';
import {
  buildOverridesFormRecord,
  CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY,
  ClientConfigKeyRecord,
  fetchClientConfigKeysByPlatform,
  saveClientConfigDefaults,
} from '../../../lib/clientConfigHelpers';

export const AppConfigDefaults = () => {

  const [definition, setDefinition] = useState<AppConfigDefinitionType | null>(null);
  const [config, setConfig] = useState<AppConfigConfigType | undefined>();
  const [keysByPath, setKeysByPath] = useState<Record<string, ClientConfigKeyRecord> | undefined>(undefined);
  const [configReady, setConfigReady] = useState(false);
  const [configLoadError, setConfigLoadError] = useState(false);
  const [keysReady, setKeysReady] = useState(false);
  const [keysLoadedOk, setKeysLoadedOk] = useState(false);
  const [updateTimestamp, setUpdateTimestamp] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const isDevAdmin = authProvider.isDeveloperAdmin();

  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    let cancelled = false;
    setConfigReady(false);
    setConfigLoadError(false);
    setKeysReady(false);
    setKeysLoadedOk(false);
    setKeysByPath(undefined);

    typedHttpClient().clientConfig.clientConfigControllerResolveAppDefaultConfigV0().then((response) => {

      if (cancelled) {

        return;

      }
      setDefinition(response.data.definition ?? null);
      setConfig(response.data.config);
      setConfigReady(true);

    }).catch(() => {

      if (!cancelled) {

        setConfigLoadError(true);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      }

    });

    fetchClientConfigKeysByPlatform('app', 0)
      .then((keys) => {

        if (!cancelled) {

          setKeysByPath(keys);
          setKeysLoadedOk(true);

        }

      })
      .catch(() => {

        if (!cancelled) {

          setKeysByPath(undefined);
          setKeysLoadedOk(false);
          notify('resources.config.text.defaults_update_error', { type: 'error' });

        }

      })
      .finally(() => {

        if (!cancelled) {

          setKeysReady(true);

        }

      });

    return () => {

      cancelled = true;

    };

  }, [notify, updateTimestamp]);

  const formRecord = useMemo(() => ({

    ...(config || {}),
    ...(keysLoadedOk ? {
      [CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY]: buildOverridesFormRecord(Object.values(keysByPath || {})),
    } : {}),

  }), [config, keysByPath, keysLoadedOk]);

  const updateConfig = async (values: FieldValues) => {

    if (!definition) {

      return;

    }

    const hasError = await saveClientConfigDefaults(
      'app',
      0,
      definition,
      config as Record<string, unknown> | undefined,
      values as Record<string, unknown>,
      keysLoadedOk ? keysByPath : undefined,
    );
    if (hasError) {

      notify('resources.config.text.defaults_update_error', { type: 'error' });

    } else {

      notify('resources.config.text.defaults_updated');

    }
    setUpdateTimestamp(Date.now());

  };

  return (
    <div>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}>
        <h1 style={{ margin: 0 }}>{translate('menu.items.app-defaults')}</h1>
        <TextField
          size="small"
          placeholder={translate('ra.action.search')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <ResourceContextProvider value={'ClientConfig'}>
        {/* noValidate: collapsed groups use display:none; browser HTML5 validation cannot focus required hidden inputs */}
        {configLoadError ? (
          <Typography color="error">
            {translate('resources.config.text.defaults_update_error', { _: 'Failed to load app defaults.' })}
          </Typography>
        ) : configReady && keysReady && (
          <Form noValidate reValidateMode={'onChange'} record={formRecord} mode="onBlur" onSubmit={updateConfig} sanitizeEmptyValues>
            {definition && (
              <ClientConfigFormGenerator
                definitions={definition}
                searchQuery={searchTerm}
                keysByPath={keysLoadedOk ? keysByPath : undefined}
              />
            )}
          </Form>
        )}
      </ResourceContextProvider>
    </div>
  );

};
